import { Injectable } from "@angular/core";
import { AppDataService } from "./app-data.service";
import { LoadingService } from "../loading/loading.service";
import { AlertService } from "../alerts/alerts.service";

import { console } from "#/log";

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { InAppBrowser, DefaultWebViewOptions } from "@capacitor/inappbrowser";
import { firstValueFrom } from "rxjs";
import { AuthenticationService } from "../authentication/authentication.service";
import { BASE_64_LOGO } from "./BASE_64_LOGO";
import { AnalyticsService } from "../analytics/analytics.service";

@Injectable()
export class PdfCertificateService {
  private apiUrl =
    "https://nn3tggi4k0.execute-api.us-west-2.amazonaws.com/prod/genReleasePlanPDF";

  constructor(
    private http: HttpClient,
    private appData: AppDataService,
    private loading: LoadingService,
    private alerts: AlertService,
    private analytics: AnalyticsService,
  ) {}

  generatePdf() {
    this.loading.showSpinner("Generating PDF");
    let user = this.appData.getUser();
    console.debug(
      "building docdef for user:",
      AuthenticationService.debugUser(user),
    );
    const docDef: any = {
      folder: "spectroscopy",
      pdf: {
        content: [
          {
            image: BASE_64_LOGO,
            width: 100,
            alignment: "center",
            margin: [0, 20, 0, 0],
          },
          {
            text: ["Mastering Spectroscopy"],
            fontSize: 25,
            bold: true,
            margin: [0, 0, 0, 10],
            alignment: "center",
          },
        ],
        pageSize: "LETTER",
        pageOrientation: "landscape",
        pageMargins: 20,
      },
    };

    docDef.pdf.content.push({
      text: [user!.email],
      fontSize: 16,
      bold: false,
      margin: [0, 0, 0, 10],
      alignment: "center",
    });

    this.appData
      .getSamplesProgress()
      .pipe()
      .subscribe(async (samples) => {
        console.debug("", samples);
        let completed = [];
        for (const sample in samples) {
          if (
            samples[sample] &&
            samples[sample].hasOwnProperty("progress") &&
            samples[sample].progress === 100
          ) {
            completed.push(this.appData.getSampleMetadata(sample)!.name);
          }
        }

        this.analytics.trackCertificate(completed);

        docDef.pdf.content.push(
          {
            text: [
              completed.length +
                " Sample" +
                (completed.length !== 1 ? "s" : "") +
                " Complete",
            ],
            fontSize: 20,
            bold: false,
            margin: [0, 0, 0, 10],
            alignment: "center",
          },
          {
            text: ["Completed Samples:"],
            fontSize: 15,
            bold: false,
            margin: [0, 0, 0, 10],
            alignment: "center",
          },
          {
            stack: completed.length > 0 ? completed : ["None"],
            fontSize: 10,
            margin: [30, 0, 30, 30],
            alignment: "center",
          },
        );

        console.debug("document definition", docDef);

        const headers = new HttpHeaders({ "Content-Type": "application/json" });

        try {
          const response = await firstValueFrom(
            this.http.post(this.apiUrl, docDef, { headers }),
          );

          console.debug("got this response from the API", response);
          this.loading.dismissSpinner();

          if (typeof response === "string") {
            /** verify response is a valid URL; `new URL()` will throw if not */
            const url = new URL(response).href;

            try {
              await InAppBrowser.openInWebView({
                url,
                options: DefaultWebViewOptions,
              });
              console.debug(
                `InAppBrowser.openInWebView completed for url`,
                url,
              );
            } catch (error) {
              console.debug(
                `InAppBrowser.openInWebView failed for url`,
                url,
                `falling back to window.open()`,
                error,
              );

              // Fallback if Browser.open fails (e.g. in desktop browser)
              let openResult = window.open(url, "_blank");

              /**
               * On iOS, `window.open()` needs a direct chain of causality from
               * a user interaction so we prompt the user to open.
               **/
              if (!openResult) {
                await this.alerts.showAlert("pdf_click_to_open").then(() => {
                  openResult = window.open(url, "_blank");
                });
              } else {
                console.debug(`window.open() completed for url`, url);
              }

              if (!openResult) {
                throw new Error(`Couldn't display PDF in new window.`);
              }
            }
          } else if ("ok" in response && !response.ok) {
            await this.alerts.showAlert("internet_required");
            this.analytics.trackError(
              "pdf-generation-offline",
              undefined,
              false,
            );
          }
        } catch (error) {
          console.error("PDF generation error", error);
          this.loading.dismissSpinner();
          this.analytics.trackError("pdf-generation-failed", error, false);
          await this.alerts.showAlert("pdf_generation_failed", [
            error instanceof Error ? error.message : String(error),
          ]);
        }
      });
  }
}
